import { useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';

import { ActivitiesContentType, Subject, Years } from 'global/constants';
import { useLoggingQuery } from 'lib/apollo/apollo-hooks';
import { ActivityOutcomes } from '../activity-outcomes.constants';
import { ActivityOutcomeGroupEdit } from './content-points.constants';
import { LinkButton } from 'approot/shared/styles/buttons/link-button.style';
import ContentPointGroupEdit from './content-point-group-edit';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { updateNoteInCache } from '../../teaching-notes.apollo';
import type { TeachingLearningSequenceFragment } from '../../teaching-notes.graphql.generated';
import Tabs from 'approot/shared/tabs/tabs';
import { SEARCH_OUTCOMES } from 'approot/shared/graphql/outcomes/outcomes.graphql';
import type {
  SearchOutcomesQuery,
  SearchOutcomesQueryVariables,
} from '../../../../../shared/graphql/outcomes/outcomes.graphql.generated';
import { Box } from 'approot/shared/design-system/box/box.style';
import { BoxPanel } from 'approot/shared/styles/box-panel.style';
import { TabsWrapper } from 'approot/shared/tabs/tabs.style';
import { useGetStartupData } from 'approot/shared/graphql/startup-data/startup-data.hooks';
import { modifyActivitiesPublishChanges } from 'approot/activity-builder/activity-builder.apollo.utils';
import { modifyOutcomesInCache } from 'approot/activity-builder/logic/apollo/modify-outcomes-in-cache';
import type { UpsertActivityOutcome } from '../../../../../../gql/schema-types';
import useUpsertActivityOutcomes from '../use-upsert-activity-outcomes';

type Props = {
  years: Years[];
  subjects: Subject[];
  contentId: number;
  contentType: ActivitiesContentType;
  sectionId?: string;
  activityId?: string;
  activityOutcomes?: ActivityOutcomes | null;
  sequenceItem?: TeachingLearningSequenceFragment;
  hideQuestionTypes?: boolean;
  hideProficiencies?: boolean;
};

const ContentPointsByOutcomes = ({
  years,
  subjects,
  contentId,
  contentType,
  sectionId,
  activityId,
  activityOutcomes,
  sequenceItem,
  hideQuestionTypes,
  hideProficiencies,
}: Props) => {
  const client = useApolloClient();

  const { data } = useGetStartupData();

  const curriculumOptions = useMemo(() => {
    return (
      data?.startupData.curriculums.map(c => ({
        label: c.name,
        value: c.name,
      })) || []
    );
  }, [data?.startupData.curriculums]);

  const [tab, setTab] = useState<string>(curriculumOptions?.[0]?.value || '');
  const [isAddNew, setIsAddNew] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<number | null>(null);

  const { upsertActivityOutcomes, loading } = useUpsertActivityOutcomes();

  const { data: outcomesData } = useLoggingQuery<
    SearchOutcomesQuery,
    SearchOutcomesQueryVariables
  >(SEARCH_OUTCOMES, {
    variables: {
      input: {
        subjects,
        years,
      },
    },
  });

  const options = useMemo(() => {
    return (
      outcomesData?.searchOutcomes
        .filter(o => o.curriculum.toLowerCase() === tab.toLowerCase())
        .map(outcome => ({
          id: outcome.id,
          label: outcome.code,
          description: outcome.description,
        })) || []
    );
  }, [outcomesData, tab]);

  const filteredActivityOutcomes = useMemo(() => {
    if (!activityOutcomes?.outcomes) {
      return [];
    }

    return activityOutcomes.outcomes.filter(
      o => o.outcome.curriculum?.toLowerCase() === tab.toLowerCase()
    );
  }, [activityOutcomes?.outcomes, tab]);

  const selectedOutcomes = useMemo(() => {
    return (
      filteredActivityOutcomes.map(o => ({
        id: o.outcomeId,
        label: o.outcome.code,
      })) || []
    );
  }, [filteredActivityOutcomes]);

  const getAvailableOptions = (excludeOutcomeId?: number) => {
    const selectedOutcomeIds = selectedOutcomes.map(o => o.id) || [];

    return options.filter(option => {
      // Don't exclude the current outcome being edited
      if (excludeOutcomeId && option.id === excludeOutcomeId) {
        return true;
      }
      // Exclude all other already selected outcomes
      return !selectedOutcomeIds.includes(option.id);
    });
  };

  const handleUpdateGroup = (
    group: ActivityOutcomeGroupEdit,
    previousOutcomeId?: number
  ) => {
    // Is a replace operation
    const isReplace = previousOutcomeId !== group.outcomeId;
    let updatedOutcomes: UpsertActivityOutcome[] = [];

    if (isReplace) {
      // Remove the previous outcome from the list
      updatedOutcomes =
        activityOutcomes?.outcomes
          .filter(g => g.outcomeId !== previousOutcomeId)
          ?.map(g => {
            return {
              outcomeId: g.outcomeId,
              questionTypes: g.questionTypes,
              proficiencies: g.proficiencies,
              contentPointIds: g.contentPoints.map(cp => cp.id),
            };
          }) || [];
      updatedOutcomes.push({
        outcomeId: group.outcomeId,
        questionTypes: group.questionTypes,
        proficiencies: group.proficiencies,
        contentPointIds: group.contentPointIds,
      });
    } else {
      updatedOutcomes =
        activityOutcomes?.outcomes?.map(g => {
          if (g.outcomeId === group.outcomeId) {
            return {
              outcomeId: group.outcomeId,
              questionTypes: group.questionTypes,
              proficiencies: group.proficiencies,
              contentPointIds: group.contentPointIds,
            };
          }
          return {
            outcomeId: g.outcomeId,
            questionTypes: g.questionTypes,
            proficiencies: g.proficiencies,
            contentPointIds: g.contentPoints.map(cp => cp.id),
          };
        }) || [];
    }

    upsertActivityOutcomes({
      variables: {
        input: {
          sectionId,
          activityId,
          contentId,
          contentType,
          outcomes: updatedOutcomes,
        },
      },
    }).then(res => {
      const data = res.data?.upsertActivityOutcomes;
      if (data) {
        modifyActivitiesPublishChanges({
          client,
          id: contentId,
          contentType,
        });
        // if sectionId and activityId are provided, update the note in the cache
        if (sequenceItem && sectionId && activityId) {
          updateNoteInCache({
            client,
            note: {
              ...sequenceItem,
              activityOutcomes: data,
            },
          });
        } else {
          modifyOutcomesInCache({
            client,
            id: contentId,
            outcomes: data.outcomes,
            contentType,
          });
        }
      }
      setEditingItem(null);
    });
  };

  /**
   * Add a new Content Groups. Merge with existing content groups. Update the Teaching and Learning Sequence cache to include the new group.
   * @param group
   */
  const handleAddNewGroup = (group: ActivityOutcomeGroupEdit) => {
    const existingOutcomes =
      activityOutcomes?.outcomes.map(outcome => {
        return {
          outcomeId: outcome.outcomeId,
          questionTypes: outcome.questionTypes,
          proficiencies: outcome.proficiencies,
          contentPointIds: outcome.contentPoints.map(cp => cp.id),
        };
      }) || [];
    const newOutcomes = [
      ...existingOutcomes,
      {
        outcomeId: group.outcomeId,
        questionTypes: group.questionTypes,
        proficiencies: group.proficiencies,
        contentPointIds: group.contentPointIds,
      },
    ];
    upsertActivityOutcomes({
      variables: {
        input: {
          contentId,
          contentType,
          outcomes: newOutcomes,
          sectionId,
          activityId,
        },
      },
    }).then(res => {
      const data = res.data?.upsertActivityOutcomes;
      if (sequenceItem && data && sectionId && activityId) {
        updateNoteInCache({
          client,
          note: {
            ...sequenceItem,
            activityOutcomes: data,
          },
        });
      } else if (data) {
        modifyOutcomesInCache({
          client,
          id: contentId,
          outcomes: data.outcomes,
          contentType,
        });
      }
      modifyActivitiesPublishChanges({
        client,
        id: contentId,
        contentType,
      });
      setEditingItem(null);
      setIsAddNew(false);
    });
  };

  /**
   * Delete outcome and content points. Remove the group from the Teaching and Learning Sequence cache.
   * @param group
   */
  const handleDeleteGroup = (group: ActivityOutcomeGroupEdit) => {
    upsertActivityOutcomes({
      variables: {
        input: {
          contentId,
          contentType,
          outcomes:
            activityOutcomes?.outcomes
              .filter(g => g.outcomeId !== group.outcomeId)
              .map(outcome => {
                return {
                  outcomeId: outcome.outcomeId,
                  questionTypes: outcome.questionTypes,
                  proficiencies: outcome.proficiencies,
                  contentPointIds: outcome.contentPoints.map(cp => cp.id),
                };
              }) || [],
          sectionId,
          activityId,
        },
      },
    }).then(res => {
      const data = res.data?.upsertActivityOutcomes;
      if (sequenceItem && data && sectionId && activityId) {
        updateNoteInCache({
          client,
          note: {
            ...sequenceItem,
            activityOutcomes: data,
          },
        });
      } else if (data) {
        modifyOutcomesInCache({
          client,
          id: contentId,
          outcomes: data.outcomes,
          contentType,
        });
      }
      modifyActivitiesPublishChanges({
        client,
        id: contentId,
        contentType,
      });
    });
  };

  return (
    <SettingsGroup as="fieldset">
      <SettingsLabel as="legend">
        {sectionId ? 'Key Activity Information' : 'Outcomes'}
      </SettingsLabel>
      <Box $marginBottom={16}>
        <TabsWrapper>
          <Tabs
            fillWidth={false}
            options={curriculumOptions}
            activeTab={tab}
            ariaDescription="Curriculum under which to apply content points"
            contentPanelId="tab-content"
            onSelect={tab => {
              setTab(tab.value as string);
              setEditingItem(null);
              if (isAddNew) {
                setIsAddNew(false);
              }
            }}
          />
        </TabsWrapper>
      </Box>
      {filteredActivityOutcomes.map((outcome, index) => (
        <BoxPanel key={outcome.outcomeId} $marginBottom={12} $isOverflowVisible>
          <ContentPointGroupEdit
            index={index}
            isNew={false}
            options={getAvailableOptions(outcome.outcomeId)}
            selectedOptionValues={selectedOutcomes}
            data={{
              outcome: outcome.outcome,
              outcomeId: outcome.outcomeId,
              questionTypes: outcome.questionTypes,
              contentPoints: outcome.contentPoints,
              proficiencies: outcome.proficiencies,
              cerTypes: outcome.cerTypes,
              parentOutcomeId: outcome.parentOutcomeId,
              __typename: 'ActivityOutcome',
            }}
            isSaving={loading}
            isEditMode={editingItem === outcome.outcomeId}
            isItemBeingEdited={!!editingItem || isAddNew}
            currentCurriculum={tab}
            hideQuestionTypes={hideQuestionTypes}
            hideProficiencies={hideProficiencies}
            setIsEditMode={() => setEditingItem(outcome.outcomeId)}
            onCancel={() => setEditingItem(null)}
            handleUpdateOutcome={handleUpdateGroup}
            handleDeleteOutcome={handleDeleteGroup}
          />
        </BoxPanel>
      ))}
      {isAddNew && (
        <BoxPanel $marginBottom={12} $isOverflowVisible>
          <ContentPointGroupEdit
            index={filteredActivityOutcomes.length + 1}
            options={getAvailableOptions()}
            selectedOptionValues={selectedOutcomes}
            isNew
            isEditMode
            isSaving={loading}
            currentCurriculum={tab}
            hideQuestionTypes={hideQuestionTypes}
            hideProficiencies={hideProficiencies}
            handleUpdateOutcome={group => handleAddNewGroup(group)}
            onCancel={() => setIsAddNew(false)}
          />
        </BoxPanel>
      )}
      <div>
        <LinkButton
          type="button"
          disabled={isAddNew || !!editingItem}
          onClick={() => setIsAddNew(true)}
        >
          {filteredActivityOutcomes.length === 0 ? 'Add' : 'Add Another'} {tab}{' '}
          Outcome
        </LinkButton>
      </div>
    </SettingsGroup>
  );
};

export default ContentPointsByOutcomes;
